export const REGISTRATION_RETURN_URL_PARAM = "returnUrl";
export const REGISTRATION_FRESH_START_PARAM = "freshStart";

/**
 * The v4 registration flow opens in a new tab, so `history.back()` cannot
 * bring the user back to the originating v3 page. Instead we pass the full
 * current page URL (e.g. https://v3-uat.kalyanmatrimony.com/fbregister8) as a
 * `returnUrl` query param. The v4 project stores it and redirects back to it
 * from the "Done" button on the expert support completion page.
 */
export const getRegistrationReturnUrlValue = () => {
  try {
    return window.location.href || "";
  } catch {
    return "";
  }
};

/**
 * Append the current page URL as the `returnUrl` param to a registration URL.
 * @param {URL} registrationUrl - a URL instance for the v4 registration page
 * @returns {URL} the same URL instance, with `returnUrl` set when available
 */
export const appendRegistrationReturnUrl = (registrationUrl) => {
  if (!registrationUrl || typeof registrationUrl.searchParams?.set !== "function") {
    return registrationUrl;
  }

  const sourceUrl = getRegistrationReturnUrlValue();
  if (sourceUrl) {
    registrationUrl.searchParams.set(REGISTRATION_RETURN_URL_PARAM, sourceUrl);
  }

  return registrationUrl;
};

/**
 * Tell the v4 registration app to discard any prior draft/localStorage and
 * start a brand-new registration. Required because v3 and register apps are
 * different origins, so v3 cannot clear register's localStorage directly.
 * Use on Register-button clicks and 4-field landing submit handoffs.
 * Do NOT use this on login-via / incomplete-profile resume handoffs.
 * @param {URL} registrationUrl
 * @returns {URL}
 */
export const appendRegistrationFreshStart = (registrationUrl) => {
  if (!registrationUrl || typeof registrationUrl.searchParams?.set !== "function") {
    return registrationUrl;
  }

  registrationUrl.searchParams.set(REGISTRATION_FRESH_START_PARAM, "1");
  return registrationUrl;
};

/**
 * Build a registration redirect URL string with the `returnUrl` param appended,
 * preserving any existing query params already present on the base URL.
 * Pass `{ freshStart: true }` (default) for Register-button clicks so leftover
 * draft autofill from a previous registration is cleared on the v4 app.
 * @param {string} baseUrl - the v4 registration base URL
 * @param {{ freshStart?: boolean }} [options]
 * @returns {string} the final URL string
 */
export const withRegistrationReturnUrl = (baseUrl, { freshStart = true } = {}) => {
  try {
    const registrationUrl = new URL(baseUrl, window.location.origin);
    appendRegistrationReturnUrl(registrationUrl);
    if (freshStart) {
      appendRegistrationFreshStart(registrationUrl);
    }
    return registrationUrl.toString();
  } catch {
    return baseUrl;
  }
};
